import Axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { api_url } from "../../../config";
import { DELAI_FRAPPE, ENDPOINT_RECHERCHE_RAPIDE, estRecherchable, nettoyerSaisie } from "./saisieEtSuggestions";

// ============================================================
// État de l'autocomplétion : saisie, suggestions, option active.
// - La requête part DELAI_FRAPPE ms après la dernière frappe ; une frappe
//   annule la requête précédente (AbortController) et une réponse tardive
//   est ignorée : les suggestions correspondent toujours à la saisie.
// - Les suggestions d'une saisie déjà faite reviennent sans appel (retour
//   arrière, réouverture).
// - Pendant le chargement, les suggestions précédentes restent affichées
//   mais aucune n'est active ; Entrée tapée à ce moment ouvre la première
//   suggestion dès qu'elle arrive.
// États : "vide", "trop-court", "chargement", "pret", "erreur".
// ============================================================

const AUCUN = { allocataires: [], plusDeResultats: false };
const TAILLE_CACHE = 30;

export default function useRechercheRapide({ delai = DELAI_FRAPPE, onValidationDifferee = () => {} } = {}) {
  const [saisie, setSaisie] = useState("");
  const [resultat, setResultat] = useState(AUCUN);
  const [etat, setEtat] = useState("vide");
  const [actif, setActif] = useState(-1);
  const [tentative, setTentative] = useState(0);
  const cache = useRef(new Map());
  const requete = useRef(null);
  const validationEnAttente = useRef(false);
  const surValidation = useRef(onValidationDifferee);
  surValidation.current = onValidationDifferee;

  const annuler = useCallback(() => {
    if (requete.current) requete.current.abort();
    requete.current = null;
  }, []);

  const appliquer = useCallback((suite) => {
    setResultat(suite);
    setEtat("pret");
    setActif(suite.allocataires.length ? 0 : -1);
    if (validationEnAttente.current) {
      validationEnAttente.current = false;
      if (suite.allocataires.length) surValidation.current(suite.allocataires[0]);
    }
  }, []);

  useEffect(() => {
    const valeur = nettoyerSaisie(saisie);
    validationEnAttente.current = false;
    // Une nouvelle saisie périme la requête en cours : seule la dernière répond.
    annuler();
    if (!estRecherchable(valeur)) {
      setResultat(AUCUN);
      setActif(-1);
      setEtat(valeur ? "trop-court" : "vide");
      return undefined;
    }
    const connue = cache.current.get(valeur);
    if (connue) {
      appliquer(connue);
      return undefined;
    }

    setEtat("chargement");
    setActif(-1);
    const minuterie = setTimeout(() => {
      const controleur = new AbortController();
      requete.current = controleur;
      Axios.get(api_url + ENDPOINT_RECHERCHE_RAPIDE, { params: { saisie: valeur }, signal: controleur.signal })
        .then((reponse) => {
          if (requete.current !== controleur) return;
          requete.current = null;
          const donnees = (reponse && reponse.data) || {};
          const suite = {
            allocataires: Array.isArray(donnees.allocataires) ? donnees.allocataires : [],
            plusDeResultats: Boolean(donnees.plusDeResultats),
          };
          cache.current.set(valeur, suite);
          if (cache.current.size > TAILLE_CACHE) cache.current.delete(cache.current.keys().next().value);
          appliquer(suite);
        })
        .catch(() => {
          if (requete.current !== controleur) return;
          requete.current = null;
          validationEnAttente.current = false;
          setResultat(AUCUN);
          setActif(-1);
          setEtat("erreur");
        });
    }, delai);
    return () => clearTimeout(minuterie);
  }, [saisie, tentative, delai, annuler, appliquer]);

  useEffect(() => annuler, [annuler]);

  const nombre = resultat.allocataires.length;
  const deplacer = useCallback(
    (pas) => setActif((courant) => (nombre ? (courant + pas + nombre) % nombre : -1)),
    [nombre]
  );

  return {
    saisie,
    setSaisie,
    etat,
    allocataires: resultat.allocataires,
    plusDeResultats: resultat.plusDeResultats,
    actif,
    setActif,
    deplacer,
    reessayer: () => setTentative((n) => n + 1),
    // Entrée pendant le chargement : la première suggestion s'ouvrira à l'arrivée.
    validerDesQuePret: () => {
      validationEnAttente.current = true;
    },
  };
}
